import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { DocumentTemplateService } from '../../services/document-template.service';

export interface ReprocessTemplateDialogData {
  s3Key: string;
  currentTemplateId: string | null | undefined;
  fileName: string | null | undefined;
}

@Component({
  selector: 'app-reprocess-template-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>Reprocess document</h2>
    <mat-dialog-content class="!pt-2">
      <p class="text-sm text-gray-700 dark:text-gray-300 mb-2">
        This will clear the existing OCR results and judge verdict for
        <strong>{{ data.fileName || data.s3Key }}</strong> and re-run OCR using the selected template.
      </p>
      @if (currentTemplateName(); as cur) {
        <p class="text-xs text-gray-600 dark:text-gray-400 mb-4">Current template: <strong>{{ cur }}</strong></p>
      }
      <mat-form-field appearance="outline" class="w-full">
        <mat-label>Template</mat-label>
        <mat-select [(value)]="selectedTemplateId">
          @for (tpl of templates(); track tpl.id) {
            <mat-option [value]="tpl.id">
              {{ tpl.name }}
              @if (tpl.id === data.currentTemplateId) { (current) }
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (errorMessage()) {
        <p class="text-sm text-red-600 dark:text-red-400 mt-2">{{ errorMessage() }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [disabled]="submitting()" (click)="cancel()">Cancel</button>
      <button mat-flat-button color="primary"
              [disabled]="!canSubmit() || submitting()"
              (click)="confirm()">
        @if (submitting()) {
          <mat-progress-spinner diameter="16" mode="indeterminate" class="inline-block align-middle mr-2"></mat-progress-spinner>
        }
        Reprocess
      </button>
    </mat-dialog-actions>
  `
})
export class ReprocessTemplateDialogComponent {
  readonly data = inject<ReprocessTemplateDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<ReprocessTemplateDialogComponent>);
  private store = inject(OcrPrimeStore);
  private documentTemplateService = inject(DocumentTemplateService);

  selectedTemplateId: string | null = null;
  submitting = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  readonly templates = computed(() =>
    [...this.store.templates()].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  );

  readonly currentTemplateName = computed(() => {
    const id = this.data.currentTemplateId;
    if (!id) return null;
    return this.store.templatesMap()[id]?.name ?? id;
  });

  constructor() {
    this.store.loadTemplates();
  }

  canSubmit(): boolean {
    return !!this.selectedTemplateId;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.canSubmit() || this.submitting()) return;
    this.submitting.set(true);
    this.errorMessage.set(null);

    this.documentTemplateService
      .reprocessDocument(this.data.s3Key, this.selectedTemplateId!)
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.dialogRef.close({ templateId: this.selectedTemplateId });
        },
        error: (err) => {
          console.error('Reprocess failed:', err);
          this.submitting.set(false);
          this.errorMessage.set(err?.error?.message || err?.message || 'Failed to reprocess document');
        }
      });
  }
}
